import React, { useState } from 'react';
import { ValidationResults, ValidationCheck } from '../types';

interface Props {
  results: ValidationResults;
  rawResponse?: any;
  onActionClick?: (title: string, content: string) => void;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PASS':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'FAIL':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'REVIEW_REQUIRED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Handle undefined or null status
  const displayStatus = status || 'UNKNOWN';

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(displayStatus)}`}>
      {displayStatus.replace('_', ' ')}
    </span>
  );
};

const getSignificanceNote = (check: ValidationCheck, checkType: string, status: string, severity?: string): string => {
  if (status === 'PASS') return '';
  
  // Use the actual why_this_matters from the lambda response if available
  if (check.why_this_matters) {
    return check.why_this_matters;
  }
  
  // Fallback to hardcoded messages only if no lambda response is available
  const significanceMap: Record<string, string> = {
    'missing_required_fields': 'Missing required fields result in automatic rejection. Each missing field must be completed before resubmission.',
    'date_validation': 'Date inconsistencies trigger manual review and can delay processing by 2-4 weeks while investigators verify timelines.',
    'employment_gaps': 'Unexplained employment gaps are red flags that often lead to additional interviews and background checks.',
    'residence_gaps': 'Residence gaps suggest incomplete disclosure and may result in application return for clarification.',
    'inconsistent_information': 'Inconsistencies between sections indicate potential deception and trigger enhanced scrutiny.',
    'incomplete_disclosure': 'Incomplete disclosure violations can result in clearance denial and potential legal consequences.',
    'foreign_contacts': 'Undisclosed foreign contacts are serious security concerns that often result in immediate rejection.',
    'financial_issues': 'Financial problems suggest vulnerability to coercion and typically require extensive mitigation.',
    'criminal_history': 'Criminal history issues require complete disclosure and mitigation to avoid automatic disqualification.',
    'substance_abuse': 'Substance abuse concerns trigger mandatory counseling requirements and extended processing.',
    'default': 'This issue increases the likelihood of application rejection or extended review periods.'
  };

  return significanceMap[checkType] || significanceMap['default'];
};

const ConfidenceScoreExplanation: React.FC<{ score: number }> = ({ score }) => {
  const getConfidenceLevel = (score: number) => {
    if (score >= 90) return { level: 'Very High', color: 'text-green-600', bg: 'bg-green-50' };
    if (score >= 80) return { level: 'High', color: 'text-blue-600', bg: 'bg-blue-50' };
    if (score >= 70) return { level: 'Moderate', color: 'text-yellow-600', bg: 'bg-yellow-50' };
    if (score >= 60) return { level: 'Low', color: 'text-orange-600', bg: 'bg-orange-50' };
    return { level: 'Very Low', color: 'text-red-600', bg: 'bg-red-50' };
  };

  const confidence = getConfidenceLevel(score);

  return (
    <div className={`p-4 rounded-lg border ${confidence.bg} border-gray-200 mb-4`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">AI Confidence Score</h3>
        <div className="flex items-center">
          <span className={`text-lg font-bold ${confidence.color}`}>{score}%</span>
          <span className={`ml-2 text-xs font-medium ${confidence.color}`}>({confidence.level})</span>
        </div>
      </div>
      <p className="text-xs text-gray-600 mb-2">
        This score indicates the AI's confidence in its analysis based on data completeness, consistency, and pattern recognition.
      </p>
      <div className="text-xs text-gray-700">
        <strong>What this means:</strong> {
          score >= 90 ? 'The analysis is highly reliable with comprehensive data coverage and clear patterns.' :
          score >= 80 ? 'The analysis is reliable with good data quality and identifiable patterns.' :
          score >= 70 ? 'The analysis is moderately reliable but some data gaps or ambiguities exist.' :
          score >= 60 ? 'The analysis has limitations due to incomplete data or conflicting information.' :
          'The analysis is preliminary due to significant data gaps or inconsistencies.'
        }
      </div>
    </div>
  );
};

const EnhancedValidationCheck: React.FC<{ 
  checkKey: string; 
  check: ValidationCheck; 
  severity: 'critical' | 'warning' | 'info';
  onActionClick?: (title: string, content: string) => void;
}> = ({ checkKey, check, severity, onActionClick }) => {
  const significanceNote = getSignificanceNote(check, checkKey, check.status, severity);
  
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'border-l-red-500 bg-red-50';
      case 'warning':
        return 'border-l-yellow-500 bg-yellow-50';
      default:
        return 'border-l-blue-500 bg-blue-50';
    }
  };

  const getActionInfo = (checkKey: string) => {
    const actionMap: Record<string, { title: string; content: string }> = {
      'missing_required_fields': {
        title: 'Address Missing Required Fields',
        content: 'This would take you to the specific PVQ sections where required fields are missing. You would be guided to complete all mandatory information before resubmission.'
      },
      'date_validation': {
        title: 'Fix Date Inconsistencies',
        content: 'This would take you to the Employment History and Military Service sections to resolve date overlaps and ensure timeline consistency.'
      },
      'employment_gaps': {
        title: 'Review Employment History',
        content: 'This would take you to Section 13A (Employment Activities) to review and explain any gaps in your employment history.'
      },
      'residence_gaps': {
        title: 'Review Residence History',
        content: 'This would take you to Section 11 (Where You Have Lived) to ensure complete residence coverage without gaps.'
      },
      'verifier_contacts': {
        title: 'Update Verifier Information',
        content: 'This would take you to the relevant sections to provide complete contact information for all verifiers and remove any relatives as verifiers.'
      },
      'relative_verifiers': {
        title: 'Replace Relative Verifiers',
        content: 'This would take you to employment and education sections to replace any relatives listed as verifiers with non-family members.'
      },
      'birth_citizenship_consistency': {
        title: 'Review Citizenship Information',
        content: 'This would take you to Section 9 (Citizenship Information) to ensure your birth location and citizenship status are consistent.'
      },
      'employment_military_dates': {
        title: 'Align Military and Employment Dates',
        content: 'This would take you to Sections 13A and 15 to ensure military service dates align properly with employment history.'
      },
      'default': {
        title: 'Address This Issue',
        content: 'This would take you to the relevant PVQ section to address this specific validation finding.'
      }
    };

    return actionMap[checkKey] || actionMap['default'];
  };

  const handleActionClick = () => {
    if (onActionClick) {
      const actionInfo = getActionInfo(checkKey);
      onActionClick(actionInfo.title, actionInfo.content);
    }
  };

  return (
    <div className={`border-l-4 ${getSeverityColor(severity)} p-4 mb-4`}>
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-900">{check.description}</h4>
        <div className="flex items-center gap-2">
          <StatusBadge status={check.status} />
          {check.status !== 'PASS' && onActionClick && (
            <button
              onClick={handleActionClick}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Fix Issue
            </button>
          )}
        </div>
      </div>
      
      {check.details && (
        <p className="text-sm text-gray-700 mb-2">{check.details}</p>
      )}
      
      {significanceNote && check.status !== 'PASS' && (
        <div className="mt-3 p-3 bg-white rounded-md border border-gray-200">
          <div className="flex items-start">
            <svg className="w-4 h-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-xs font-medium text-amber-900 mb-1">Why This Matters:</p>
              <p className="text-xs text-amber-800">{significanceNote}</p>
            </div>
          </div>
        </div>
      )}
      
      {check.how_to_address && check.status !== 'PASS' && (
        <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
          <div className="flex items-start">
            <svg className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-xs font-medium text-blue-900 mb-1">How to Address:</p>
              <p className="text-xs text-blue-800">{check.how_to_address}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EnhancedValidationResultsDisplay: React.FC<Props> = ({ results, rawResponse, onActionClick }) => {
  const [showRawResponse, setShowRawResponse] = useState(false);

  // Extract confidence score from raw response
  const confidenceScore = rawResponse?.confidence_score || rawResponse?.metadata?.confidence_score || 85;

  return (
    <div className="space-y-6">
      {/* Confidence Score */}
      <ConfidenceScoreExplanation score={confidenceScore} />

      {/* Why This Matters - Moved to Top */}
      <div className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
        <div className="flex items-start">
          <div className="flex items-center justify-center w-10 h-10 bg-amber-100 rounded-full mr-4 mt-1">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-amber-900 mb-3">Understanding Your Results:</h4>
            <div className="text-amber-800 text-sm space-y-2">
              <p>
                <strong>Critical Issues</strong> require immediate attention - they typically result in 85-95% rejection rates if unresolved.
              </p>
              <p>
                <strong>Warnings</strong> increase processing time and scrutiny - addressing them reduces delays by up to 40%.
              </p>
              <p>
                Each finding below includes specific impact information to help you prioritize your response.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Critical Issues */}
      {results.critical_issues && (
        (Array.isArray(results.critical_issues) && results.critical_issues.length > 0) ||
        (typeof results.critical_issues === 'object' && !Array.isArray(results.critical_issues) && Object.keys(results.critical_issues).length > 0)
      ) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full mr-3">
              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-900">Critical Issues</h3>
          </div>
          <div className="space-y-3">
            {Array.isArray(results.critical_issues) 
              ? results.critical_issues.map((issue, index) => (
                  <EnhancedValidationCheck 
                    key={`critical-${index}`} 
                    checkKey={`critical_issue_${index}`} 
                    check={{ status: 'FAIL' as const, description: issue, details: '' }} 
                    severity="critical" 
                    onActionClick={onActionClick}
                  />
                ))
              : Object.entries(results.critical_issues).map(([key, check]) => {
                  // Ensure check is an object with required properties
                  const validCheck = typeof check === 'object' && check !== null ? check : 
                    { status: 'REVIEW_REQUIRED' as const, description: String(check) || 'Unknown issue', details: '' };
                  
                  return (
                    <EnhancedValidationCheck 
                      key={key} 
                      checkKey={key} 
                      check={validCheck} 
                      severity="critical" 
                      onActionClick={onActionClick}
                    />
                  );
                })
            }
          </div>
        </div>
      )}

      {/* Warnings */}
      {results.warnings && (
        (Array.isArray(results.warnings) && results.warnings.length > 0) ||
        (typeof results.warnings === 'object' && !Array.isArray(results.warnings) && Object.keys(results.warnings).length > 0)
      ) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-full mr-3">
              <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-yellow-900">Warnings</h3>
          </div>
          <div className="space-y-3">
            {Array.isArray(results.warnings) 
              ? results.warnings.map((warning, index) => (
                  <EnhancedValidationCheck 
                    key={`warning-${index}`} 
                    checkKey={`warning_${index}`} 
                    check={{ status: 'REVIEW_REQUIRED' as const, description: warning, details: '' }} 
                    severity="warning" 
                    onActionClick={onActionClick}
                  />
                ))
              : Object.entries(results.warnings).map(([key, check]) => {
                  // Ensure check is an object with required properties
                  const validCheck = typeof check === 'object' && check !== null ? check : 
                    { status: 'REVIEW_REQUIRED' as const, description: String(check) || 'Unknown warning', details: '' };
                  
                  return (
                    <EnhancedValidationCheck 
                      key={key} 
                      checkKey={key} 
                      check={validCheck} 
                      severity="warning" 
                      onActionClick={onActionClick}
                    />
                  );
                })
            }
          </div>
        </div>
      )}

      {/* Recommendations */}
      {results.recommendations && (
        (Array.isArray(results.recommendations) && results.recommendations.length > 0) ||
        (typeof results.recommendations === 'object' && !Array.isArray(results.recommendations) && Object.keys(results.recommendations).length > 0)
      ) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-blue-900">Recommendations</h3>
          </div>
          <div className="space-y-3">
            {Array.isArray(results.recommendations) 
              ? results.recommendations.map((recommendation, index) => (
                  <EnhancedValidationCheck 
                    key={`recommendation-${index}`} 
                    checkKey={`recommendation_${index}`} 
                    check={{ status: 'PASS' as const, description: recommendation, details: '' }} 
                    severity="info" 
                    onActionClick={onActionClick}
                  />
                ))
              : Object.entries(results.recommendations).map(([key, check]) => {
                  // Ensure check is an object with required properties
                  const validCheck = typeof check === 'object' && check !== null ? check : 
                    { status: 'REVIEW_REQUIRED' as const, description: String(check) || 'Unknown recommendation', details: '' };
                  
                  return (
                    <EnhancedValidationCheck 
                      key={key} 
                      checkKey={key} 
                      check={validCheck} 
                      severity="info" 
                      onActionClick={onActionClick}
                    />
                  );
                })
            }
          </div>
        </div>
      )}

      {/* Cross Section Validations */}
      {results.cross_section_validations && Object.keys(results.cross_section_validations).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full mr-3">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-purple-900">Cross-Section Validations</h3>
          </div>
          <div className="space-y-3">
            {Object.entries(results.cross_section_validations).map(([key, check]) => (
              <EnhancedValidationCheck 
                key={key} 
                checkKey={key} 
                check={check} 
                severity={check.status === 'FAIL' ? 'critical' : check.status === 'REVIEW_REQUIRED' ? 'warning' : 'info'} 
                onActionClick={onActionClick}
              />
            ))}
          </div>
        </div>
      )}

      {/* Verifier Checks */}
      {results.verifier_checks && Object.keys(results.verifier_checks).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-full mr-3">
              <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-indigo-900">Verifier Checks</h3>
          </div>
          <div className="space-y-3">
            {Object.entries(results.verifier_checks).map(([key, check]) => (
              <EnhancedValidationCheck 
                key={key} 
                checkKey={key} 
                check={check} 
                severity={check.status === 'FAIL' ? 'critical' : check.status === 'REVIEW_REQUIRED' ? 'warning' : 'info'} 
                onActionClick={onActionClick}
              />
            ))}
          </div>
        </div>
      )}

      {/* Selection Logic */}
      {results.selection_logic && Object.keys(results.selection_logic).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full mr-3">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-green-900">Selection Logic</h3>
          </div>
          <div className="space-y-3">
            {Object.entries(results.selection_logic).map(([key, check]) => (
              <EnhancedValidationCheck 
                key={key} 
                checkKey={key} 
                check={check} 
                severity={check.status === 'FAIL' ? 'critical' : check.status === 'REVIEW_REQUIRED' ? 'warning' : 'info'} 
                onActionClick={onActionClick}
              />
            ))}
          </div>
        </div>
      )}

      {/* Raw Response Toggle */}
      {rawResponse && (
        <div className="bg-gray-50 rounded-lg p-4">
          <button
            onClick={() => setShowRawResponse(!showRawResponse)}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <svg className={`w-4 h-4 mr-2 transform transition-transform ${showRawResponse ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showRawResponse ? 'Hide' : 'Show'} Raw Response
          </button>
          {showRawResponse && (
            <pre className="mt-4 text-xs bg-gray-100 p-4 rounded overflow-x-auto">
              {JSON.stringify(rawResponse, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedValidationResultsDisplay;